import React, { useEffect, useRef, useState } from 'react';
import './select.css';

interface ISelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface ISelectProps {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    options: ISelectOption[];
    className?: string;
    required?: boolean;
    error?: string;
    disabled?: boolean;
    placeholder?: string;
    size?: 'small' | 'medium' | 'large';
    variant?: 'default' | 'filled' | 'outlined';
    helperText?: string;
    leftIcon?: React.ReactNode;
    id?: string;
    name?: string;
}

// Custom listbox (trigger + floating menu) — a native <select>'s open menu can't
// be restyled by CSS, so this renders its own menu, checkmark and scrollbar instead.
const Select: React.FC<ISelectProps> = ({
    label,
    value,
    onChange,
    options,
    className = '',
    required = false,
    error,
    disabled = false,
    placeholder = '-- Select an option --',
    size = 'medium',
    variant = 'default',
    helperText,
    leftIcon,
    id,
    name,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const selectId = id || name || `select-${Math.random().toString(36).substr(2, 9)}`;

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const sizes = {
        small: 'px-3.5 py-2 text-[var(--font-secondary)] min-h-[38px]',
        medium: 'px-4 py-2.5 text-[var(--font-body)] min-h-[44px]',
        large: 'px-4.5 py-3 text-base min-h-[50px]',
    };

    const variants = {
        default: `border bg-[var(--bg-surface)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] ${error
            ? 'border-[var(--danger)]'
            : open
                ? 'border-[var(--primary-500)] ring-[3px] ring-[var(--primary-500)]/20'
                : 'border-[var(--border-subtle)] hover:border-[var(--border-glass-strong)]'
            }`,
        filled: `border-0 bg-[var(--bg-hover)] ${error
            ? 'bg-[var(--danger-light)]'
            : open
                ? 'bg-[var(--bg-card)] ring-[3px] ring-[var(--primary-500)]/20'
                : 'hover:bg-[var(--bg-card)]'
            }`,
        outlined: `border-2 bg-transparent ${error
            ? 'border-[var(--danger)]'
            : open
                ? 'border-[var(--primary-500)] ring-[3px] ring-[var(--primary-500)]/20'
                : 'border-[var(--border-subtle)] hover:border-[var(--border-glass-strong)]'
            }`,
    };

    const iconSize = size === 'small' ? 'w-4 h-4' : size === 'large' ? 'w-5 h-5' : 'w-4 h-4';
    const iconPadding = leftIcon ? (size === 'large' ? 'pl-11' : 'pl-10') : '';
    const selected = options.find(o => o.value === value);

    return (
        <div className={`w-full ${className}`} ref={rootRef}>
            {label && (
                <label
                    htmlFor={selectId}
                    className="block mb-2.5 text-[var(--font-secondary)] font-semibold tracking-[-0.005em] text-[var(--text-secondary)]"
                >
                    {label}
                    {required && <span className="text-[var(--danger)] ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                {leftIcon && (
                    <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] pointer-events-none z-10 ${iconSize}`}>
                        {leftIcon}
                    </div>
                )}

                <button
                    id={selectId}
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen(o => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    className={`
                        w-full flex items-center justify-between gap-2 rounded-[var(--radius-md)] transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
                        focus:outline-none cursor-pointer font-normal text-left
                        disabled:bg-[var(--bg-hover)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed disabled:opacity-60
                        ${sizes[size]}
                        ${variants[variant]}
                        ${iconPadding}
                        ${error ? 'text-[var(--danger)]' : 'text-[var(--text-secondary)]'}
                    `}
                >
                    <span className={`truncate ${selected ? '' : 'text-[var(--text-muted)]'}`}>
                        {selected ? selected.label : placeholder}
                    </span>
                    <svg className={`${iconSize} shrink-0 text-[var(--text-muted)] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {open && (
                    <div
                        role="listbox"
                        className="lab-select-menu absolute z-50 mt-1.5 w-full max-h-64 overflow-y-auto rounded-[var(--radius-lg)] border p-1.5"
                        style={{
                            background: 'var(--bg-elevated, var(--bg-card))',
                            borderColor: 'var(--border-subtle)',
                            boxShadow: 'var(--shadow-premium, 0 8px 32px rgba(0,0,0,0.28))',
                        }}
                    >
                        {options.map((opt) => {
                            const isSelected = opt.value === value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    disabled={opt.disabled}
                                    onClick={() => { if (opt.disabled) return; onChange(opt.value); setOpen(false); }}
                                    className="w-full flex items-center gap-2 rounded-[var(--radius-sm)] px-3 py-1.5 text-[13px] text-left cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                    style={{
                                        color: isSelected ? 'var(--primary-600)' : 'var(--text-primary)',
                                        fontWeight: isSelected ? 650 : 450,
                                        background: isSelected ? 'color-mix(in srgb, var(--primary-500) 10%, transparent)' : 'transparent',
                                    }}
                                    onMouseEnter={(e) => { if (!opt.disabled && !isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                                    onMouseLeave={(e) => { if (!opt.disabled && !isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                >
                                    <svg
                                        className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                                        fill="none" stroke="var(--primary-500)" viewBox="0 0 24 24" strokeWidth={2.5}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="flex-1 truncate">{opt.label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="mt-1.5">
                {error ? (
                    <p className="text-[var(--font-secondary)] text-[var(--danger)]/80 font-medium flex items-center gap-1">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </p>
                ) : helperText ? (
                    <p className="text-[var(--font-secondary)] text-[var(--text-secondary)]">{helperText}</p>
                ) : null}
            </div>
        </div>
    );
};

export default Select;
